import React, { FC, useMemo } from "react";
import EntityTextVisual from "../text/EntityTextVisual";
import { Validation } from "../../../validation/constants";
import useEntityAnnotation from "../../../../configMap/hooks/useEntityAnnotation";
import useEntityNormalization from "../../../../configMap/hooks/useEntityNormalization";
import { GroupBlockEntityType } from "../../../../configMap";
import { EntityAnnotation } from "../../../../annotation";
import VALIDATIONS_DATE from "./validations";
import EntityValidation from "../../../../configMap/components/entity/EntityValidation";
import dayjs from "dayjs";
import { VALID_DATE_FORMATS } from "../../../utilities/formats/dateFormats";
import EditEntityDate from "./EditEntityDate";

export type DateEntityProps = {
  entityType: GroupBlockEntityType;
  entityAnnotation: EntityAnnotation;
  isEditing: boolean;
  isAnnotating: boolean;
  groupBlockIndex: number;
  documentId?: string;
  isTableMode?: boolean;
  // SideControls specific props
  showLabels?: boolean;
  isEditable?: boolean;
  showNormalizedValue?: boolean;
  requiresAttention?: boolean;
  // TableControls specific props
  cellClasses?: string;
};

const DateEntity: FC<DateEntityProps> = ({
  entityType,
  entityAnnotation,
  isEditing,
  isAnnotating,
  groupBlockIndex,
  documentId,
  isTableMode = false,
  showLabels = true,
  isEditable = true,
  showNormalizedValue = true,
  requiresAttention = false,
  cellClasses = "",
}) => {
  const { value, normalizedValue, errorType } =
    useEntityAnnotation(entityAnnotation);

  const { entityNormalizationFormat } = useEntityNormalization(
    entityType?.entityType?.entityNormalizations,
  );

  const mergedValidations = useMemo(
    (): Array<Validation> => [...VALIDATIONS_DATE],
    [],
  );

  if (!entityAnnotation) {
    return null;
  }

  if (documentId && isEditing && !isAnnotating && isEditable) {
    return (
      <>
        <EditEntityDate
          entityType={entityType}
          documentId={documentId}
          defaultValue={dayjs(
            normalizedValue ?? value,
            VALID_DATE_FORMATS["eu"],
          )}
          annotationId={entityAnnotation.id!}
          groupBlockIndex={groupBlockIndex}
          format={entityNormalizationFormat}
          isTableMode={isTableMode}
        />
        {isEditable && !isTableMode && (
          <EntityValidation
            validationId={`${entityType.entityType.id}-${groupBlockIndex}`}
            value={value}
            normalizedValue={normalizedValue}
            validations={mergedValidations}
            errorType={errorType}
          />
        )}
      </>
    );
  }

  return (
    <>
      <EntityTextVisual
        value={value}
        normalizedValue={normalizedValue}
        showLabels={showLabels}
        showNormalizedValue={showNormalizedValue}
        requiresAttention={requiresAttention}
        errorType={errorType}
        cellClasses={cellClasses}
        isTableMode={isTableMode}
      />
      {isEditable && !isTableMode && (
        <EntityValidation
          validationId={`${entityType.entityType.id}-${groupBlockIndex}`}
          value={value}
          normalizedValue={normalizedValue}
          validations={mergedValidations}
          errorType={errorType}
        />
      )}
    </>
  );
};

export default DateEntity;
